import React, { useEffect, useState, useRef } from 'react';
import { useTheme } from '../ThemeContext';
import { Package } from 'lucide-react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface SegmentData {
  symbol: string;
  fiscalYear: number;
  period: string;
  date: string;
  data: Record<string, number>;
}

interface ProductSegmentsProps {
  symbol?: string;
}

const ProductSegments: React.FC<ProductSegmentsProps> = ({ symbol = 'AAPL' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [segmentData, setSegmentData] = useState<SegmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'annual' | 'quarter'>('annual');
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const base = (import.meta.env.VITE_APP_API_URL as string);
        const url = `${base}/segment-and-kpi?symbol=${symbol}&period=${viewMode}&segmentType=product`;
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error('Failed to fetch segment data');
        }

        const data = await response.json();
        const sorted = data.sort((a: SegmentData, b: SegmentData) => b.fiscalYear - a.fiscalYear);
        setSegmentData(sorted);
        setError(null);

        // Get non-zero segments from the MOST RECENT year only
        if (sorted.length > 0) {
          const mostRecentYear = sorted[0]; // Most recent year data
          const nonZeroSegmentsFromRecentYear = Object.keys(mostRecentYear.data).filter(product => {
            const value = mostRecentYear.data[product] || 0;
            return value > 0; // Only segments with non-zero values in the most recent year
          });

          if (nonZeroSegmentsFromRecentYear.length > 0) {
            setSelectedProducts([nonZeroSegmentsFromRecentYear[0]]); // Select first non-zero segment
          }
        }
      } catch (err) {
        console.error('Error fetching segment data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [symbol, viewMode]);

  const formatCurrency = (value: number): string => {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toLocaleString()}`;
  };

  // Intelligent number formatting based on data magnitude
  const getOptimalUnit = (): { divisor: number; unit: string; label: string } => {
    if (segmentData.length === 0) return { divisor: 1e9, unit: 'B', label: 'Billions USD' };

    // Find the maximum value across all products and all periods
    let maxValue = 0;
    segmentData.forEach(item => {
      // If no products are selected, check all available products
      const productsToCheck = selectedProducts.length > 0 ? selectedProducts : Object.keys(item.data);
      productsToCheck.forEach(product => {
        const value = item.data[product] || 0;
        if (value > maxValue) maxValue = value;
      });
    });

    // Determine optimal unit based on maximum value
    if (maxValue >= 1e9) {
      return { divisor: 1e9, unit: 'B', label: 'Billions USD' };
    } else if (maxValue >= 1e6) {
      return { divisor: 1e6, unit: 'M', label: 'Millions USD' };
    } else {
      return { divisor: 1, unit: '', label: 'USD' };
    }
  };

  const formatNumber = (value: number): number => {
    const { divisor } = getOptimalUnit();
    return value / divisor;
  };

  // Calculate CAGR for a product segment
  const calculateCAGR = (product: string): number => {
    if (segmentData.length < 2) return 0;

    const reversedData = [...segmentData].reverse(); // Chronological order
    const nonZeroPeriods = reversedData.filter(item => {
      const value = item.data[product] || 0;
      return value > 0;
    });

    if (nonZeroPeriods.length < 2) return 0;

    const startValue = nonZeroPeriods[0].data[product] || 0;
    const endValue = nonZeroPeriods[nonZeroPeriods.length - 1].data[product] || 0;
    const years = nonZeroPeriods.length - 1;

    if (startValue <= 0 || endValue <= 0 || years <= 0) return 0;

    const cagr = (Math.pow(endValue / startValue, 1 / years) - 1) * 100;
    return Math.round(cagr * 10) / 10; // Round to 1 decimal place
  };

  const getProductCategories = (): string[] => {
    if (segmentData.length === 0) return [];

    // Get segments that have non-zero values in the MOST RECENT year only
    const mostRecentYear = segmentData[0]; // First item is most recent due to sorting
    const nonZeroSegmentsFromRecentYear = Object.keys(mostRecentYear.data).filter(product => {
      const value = mostRecentYear.data[product] || 0;
      return value > 0; // Only segments with non-zero values in the most recent year
    });

    return nonZeroSegmentsFromRecentYear;
  };

  // Prepare chart data for selected products using Highcharts
  const prepareHighchartsOptions = () => {
    if (selectedProducts.length === 0) return null;

    const reversedData = [...segmentData].reverse(); // Chronological order
    const { unit, label } = getOptimalUnit(); // Get the optimal unit for this data

    // Get only products that have non-zero values in the most recent year
    const allProducts = getProductCategories();

    // Filter out periods where ALL selected products have zero values
    const filteredPeriods = reversedData.filter(item => {
      return selectedProducts.some(product => {
        const value = item.data[product] || 0;
        return value > 0;
      });
    });

    const categories = filteredPeriods.map(item =>
      viewMode === 'quarter'
        ? `${item.period} ${item.fiscalYear}`
        : `FY ${item.fiscalYear}`
    );

    // Define distinct colors for each product
    const productColors = [
      '#3b82f6', // Blue
      '#8b5cf6', // Purple
      '#10b981', // Green
      '#f59e0b', // Orange
      '#ef4444', // Red
      '#06b6d4', // Cyan
      '#84cc16', // Lime
      '#f97316'  // Orange-red
    ];

    const series = allProducts.map((product, productIndex) => {
      const productColor = productColors[productIndex % productColors.length];
      const isSelected = selectedProducts.includes(product);
      const cagr = calculateCAGR(product);
      const cagrText = cagr !== 0 ? ` (CAGR ${cagr > 0 ? '+' : ''}${cagr}%)` : '';

      const data = filteredPeriods.map((item) => {
        const value = item.data[product] || 0;
        // Only show data for selected products, and exclude zero values completely
        if (!isSelected || value === 0) {
          return null;
        }
        return {
          y: formatNumber(value),
          color: productColor,
          rawValue: value
        };
      });

      return {
        name: `${product}${cagrText}`,
        type: 'column',
        data: data,
        color: productColor,
        visible: isSelected, // Only selected products are visible
        showInLegend: true, // All products show in legend
        dataLabels: {
          enabled: isSelected, // Only show labels for selected products
          format: '${point.y:.2f}',
          style: {
            color: isDark ? '#ffffff' : '#1f2937',
            fontSize: '11px',
            fontWeight: '600',
            textOutline: 'none'
          },
          y: -5
        }
      };
    });

    const options: Highcharts.Options = {
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'Arial, sans-serif'
        },
        height: 400
      },
      credits: {
        enabled: true,
        text: 'Powered by StockFortress',
        href: 'https://stockfortress.com',
        style: {
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: '10px'
        }
      },
      title: {
        text: ''
      },
      xAxis: {
        categories: categories,
        labels: {
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '11px'
          }
        },
        lineColor: isDark ? '#374151' : '#e5e7eb',
        tickColor: isDark ? '#374151' : '#e5e7eb'
      },
      yAxis: {
        title: {
          text: `Revenue (${label})`,
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '12px'
          }
        },
        labels: {
          format: '${value}',
          style: {
            color: isDark ? '#9ca3af' : '#6b7280',
            fontSize: '11px'
          }
        },
        gridLineColor: isDark ? '#374151' : '#e5e7eb'
      },
      tooltip: {
        backgroundColor: isDark ? '#1f2937' : '#ffffff',
        borderColor: isDark ? '#374151' : '#e5e7eb',
        style: {
          color: isDark ? '#ffffff' : '#000000'
        },
        useHTML: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const point = this.point as any;
          const seriesName = this.series.name;
          const productName = seriesName.split(' (CAGR')[0]; // Extract original product name
          const cagr = calculateCAGR(productName);
          const cagrText = cagr !== 0 ? `<br/>CAGR: <b>${cagr > 0 ? '+' : ''}${cagr}%</b>` : '';

          const formattedValue = this.y ? `$${this.y.toFixed(2)}${unit}` : 'N/A';

          return `<div style="padding: 5px;">
            ${productName}: <b>${formattedValue}</b>${cagrText}
          </div>`;
        }
      },
      legend: {
        enabled: true,
        itemStyle: {
          color: isDark ? '#e5e7eb' : '#374151',
          fontSize: '12px'
        },
        itemHoverStyle: {
          color: isDark ? '#ffffff' : '#000000'
        },
        itemHiddenStyle: {
          color: isDark ? '#6b7280' : '#9ca3af' // Gray out hidden/unselected items
        }
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          borderWidth: 0,
          groupPadding: 0.15,
          pointPadding: 0.1
        },
        series: {
          events: {
            legendItemClick: function () {
              const seriesName = this.name;
              const productName = seriesName.split(' (CAGR')[0]; // Extract original product name
              setSelectedProducts(prev => {
                if (prev.includes(productName)) {
                  return prev.filter(p => p !== productName);
                } else {
                  return [...prev, productName];
                }
              });
              return false; // Prevent default legend click behavior
            }
          }
        }
      },
      series: series as any
    };

    return options;
  };

  const handleProductToggle = (product: string) => {
    setSelectedProducts(prev => {
      if (prev.includes(product)) {
        return prev.filter(p => p !== product);
      } else {
        // Limit to 3 products for clarity
        if (prev.length >= 3) {
          return [...prev.slice(1), product];
        }
        return [...prev, product];
      }
    });

    // Scroll to chart when selecting
    setTimeout(() => {
      chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const productCategories = getProductCategories();
  const chartOptions = prepareHighchartsOptions();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-theme-secondary">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className={`ml-3 ${isDark ? 'text-theme-secondary' : 'text-gray-700'}`}>Loading product segments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 rounded-lg ${isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'} border m-4`}>
        <p className={`${isDark ? 'text-red-300' : 'text-red-700'}`}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="bg-theme-secondary px-4 pb-4 pt-2 text-theme-primary">
      {/* Chart Section */}
      {selectedProducts.length > 0 && chartOptions && (
        <div ref={chartRef} className="bg-theme-secondary rounded-lg mb-4 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold mb-1">Revenue Trends</h3>
              <p className="text-xs text-theme-secondary">
                All Product Segments - Values in {getOptimalUnit().label}
              </p>
            </div>
            {/* Annual/Quarterly Toggle moved inside chart container */}
            <div className="flex gap-1">
              <button
                onClick={() => setViewMode('annual')}
                className={`px-3 py-1 text-xs rounded-md btn ${viewMode === 'annual'
                    ? 'active'
                    : ''
                  }`}
              >
                Annual
              </button>
              <button
                onClick={() => setViewMode('quarter')}
                className={`px-3 py-1 text-xs rounded-md btn ${viewMode === 'quarter'
                    ? 'active'
                    : ''
                  }`}
              >
                Quarterly
              </button>
            </div>
          </div>

          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSegments;
